/**
 * useAgentBroadcast — Hook for AI-driven browser navigation via WebSocket.
 *
 * Subscribes to agent broadcast events (navigate, highlight, clear, stop)
 * from the chief-service MCP browser control tools (DEC-026 Tier 1).
 * Drives React Router navigation and DOM highlight animations.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

// ── Types ──

interface AgentEvent {
  type: string
  data: Record<string, unknown>
}

interface ConfirmRequest {
  confirmation_id: string
  action_description: string
  api_call: string
  tenant: string
  reversible: boolean
  timeout_seconds: number
}

export interface UseAgentBroadcastResult {
  isAgentActive: boolean
  currentAgentPage: string
  highlightCount: number
  stopAgent: () => void
  isConfirmOpen: boolean
  confirmRequest: ConfirmRequest | null
  closeConfirmDialog: () => void
  wsSend: (msg: string) => void
}

// ── Constants ──

const WS_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/v1/ws/executions`
const RECONNECT_BASE_MS = 3000
const MAX_RECONNECT_ATTEMPTS = 10
const PING_INTERVAL_MS = 30000
const AUTO_HIDE_MS = 5000
const HIGHLIGHT_DURATION_MS = 10000
const STYLE_ID = 'agent-highlight-styles'

/** Validate testid: alphanumeric, hyphens, underscores, dots, colons only */
const TESTID_RE = /^[a-zA-Z0-9][a-zA-Z0-9\-_:.]*$/

// ── CSS for highlight animation ──

const HIGHLIGHT_CSS = `
@keyframes agent-highlight-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.8); }
  50% { box-shadow: 0 0 0 6px rgba(251, 191, 36, 0.4); }
}
.agent-highlight-active {
  box-shadow: 0 0 0 3px #FBBF24 !important;
  animation: agent-highlight-pulse 1.5s ease-in-out 3;
  border-radius: inherit;
  transition: box-shadow 0.3s ease;
  position: relative;
  z-index: 1000;
}
`

// ── Helper: clear all highlights from DOM ──

function clearAllHighlights(): void {
  document.querySelectorAll('.agent-highlight-active').forEach((el) => {
    el.classList.remove('agent-highlight-active')
  })
}

// ── Hook ──

export function useAgentBroadcast(): UseAgentBroadcastResult {
  const navigate = useNavigate()

  const [isAgentActive, setIsAgentActive] = useState(false)
  const [currentAgentPage, setCurrentAgentPage] = useState('')
  const [highlightCount, setHighlightCount] = useState(0)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const mountedRef = useRef(true)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const highlightTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // ── Cleanup helpers ──

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const clearAutoHideTimer = useCallback(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current)
      autoHideTimerRef.current = null
    }
  }, [])

  const clearHighlightTimers = useCallback(() => {
    highlightTimersRef.current.forEach((t) => clearTimeout(t))
    highlightTimersRef.current = []
  }, [])

  // ── Reset all agent state ──

  const resetState = useCallback(() => {
    clearAutoHideTimer()
    clearHighlightTimers()
    clearAllHighlights()
    setIsAgentActive(false)
    setCurrentAgentPage('')
    setHighlightCount(0)
  }, [clearAutoHideTimer, clearHighlightTimers])

  // ── Confirmation dialog helpers ──

  const closeConfirmDialog = useCallback(() => {
    setIsConfirmOpen(false)
    setConfirmRequest(null)
  }, [])

  const wsSend = useCallback((msg: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg)
    }
  }, [])

  // ── Start auto-hide timer (resets on each navigation event) ──

  const startAutoHideTimer = useCallback(() => {
    clearAutoHideTimer()
    autoHideTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return
      setIsAgentActive(false)
    }, AUTO_HIDE_MS)
  }, [clearAutoHideTimer])

  // ── Event handlers ──

  const handleAgentNavigate = useCallback((data: Record<string, unknown>) => {
    const url = String(data.url || '/')
    const label = String(data.label || url)

    // Clear existing highlights — new page, fresh state
    clearHighlightTimers()
    clearAllHighlights()
    setHighlightCount(0)

    // Navigate via React Router
    navigate(url)

    // Update state
    setIsAgentActive(true)
    setCurrentAgentPage(label)
    startAutoHideTimer()
  }, [navigate, startAutoHideTimer, clearHighlightTimers])

  const handleAgentHighlight = useCallback((data: Record<string, unknown>) => {
    const testid = String(data.testid || '')

    // Validate testid to prevent CSS selector injection
    if (!testid || !TESTID_RE.test(testid)) {
      console.warn('[AgentBroadcast] Invalid testid, skipping highlight:', testid)
      return
    }

    const el = document.querySelector(`[data-testid="${testid}"]`)
    if (!el) {
      console.warn('[AgentBroadcast] Element not found:', testid)
      return
    }

    el.classList.add('agent-highlight-active')
    setHighlightCount((c) => c + 1)
    setIsAgentActive(true)
    startAutoHideTimer()

    // Auto-remove highlight after duration
    const timer = setTimeout(() => {
      el.classList.remove('agent-highlight-active')
      if (!mountedRef.current) return
      setHighlightCount((c) => Math.max(0, c - 1))
    }, HIGHLIGHT_DURATION_MS)
    highlightTimersRef.current.push(timer)
  }, [startAutoHideTimer])

  const handleAgentClearHighlights = useCallback(() => {
    clearHighlightTimers()
    clearAllHighlights()
    setHighlightCount(0)
  }, [clearHighlightTimers])

  const handleAgentStop = useCallback(() => {
    resetState()
  }, [resetState])

  // ── Message dispatcher ──

  const handleMessage = useCallback((event: MessageEvent) => {
    if (!mountedRef.current) return

    let parsed: AgentEvent
    try {
      parsed = JSON.parse(event.data as string) as AgentEvent
    } catch {
      return // Non-JSON (e.g. ping), ignore
    }

    switch (parsed.type) {
      case 'agent_navigate':
        handleAgentNavigate(parsed.data)
        break
      case 'agent_highlight':
        handleAgentHighlight(parsed.data)
        break
      case 'agent_clear_highlights':
        handleAgentClearHighlights()
        break
      case 'agent_stop':
        handleAgentStop()
        break
      case 'agent_confirm_request':
        setConfirmRequest(parsed.data as unknown as ConfirmRequest)
        setIsConfirmOpen(true)
        break
      // Other event types (agent_update, chat_stream, etc.) — not our concern
    }
  }, [handleAgentNavigate, handleAgentHighlight, handleAgentClearHighlights, handleAgentStop])

  // ── WebSocket connection ──

  const connect = useCallback(() => {
    // Build-time gate: the agent-broadcast channel is development tooling.
    // Deployment builds (bank/UAT) set VITE_ENABLE_AGENT_BROADCAST=false and
    // never open the socket (no reconnect noise, no tooling disclosure).
    if (import.meta.env.VITE_ENABLE_AGENT_BROADCAST === 'false') return
    if (!mountedRef.current) return

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    try {
      const ws = new WebSocket(WS_URL)
      wsRef.current = ws

      ws.onopen = () => {
        if (!mountedRef.current) return
        console.log('[AgentBroadcast] WebSocket connected')
        reconnectAttemptsRef.current = 0

        // Heartbeat ping
        if (pingTimerRef.current) clearInterval(pingTimerRef.current)
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send('ping') } catch { /* ignore */ }
          }
        }, PING_INTERVAL_MS)
      }

      ws.onmessage = handleMessage

      ws.onclose = () => {
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current)
          pingTimerRef.current = null
        }
        if (!mountedRef.current) return

        console.log('[AgentBroadcast] WebSocket disconnected')
        wsRef.current = null

        // Fail safe: deactivate agent mode on disconnect
        setIsAgentActive(false)

        // Auto-reconnect with backoff
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1
          const delay = RECONNECT_BASE_MS * Math.min(reconnectAttemptsRef.current, 5)
          console.log(
            `[AgentBroadcast] Reconnecting in ${delay}ms ` +
            `(attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
          )
          clearReconnectTimer()
          reconnectTimerRef.current = setTimeout(connect, delay)
        }
      }

      ws.onerror = (err) => {
        console.error('[AgentBroadcast] WebSocket error:', err)
      }
    } catch (err) {
      console.error('[AgentBroadcast] Failed to create WebSocket:', err)
    }
  }, [handleMessage, clearReconnectTimer])

  // ── Stop agent: send agent_stop via WS and reset locally ──

  const stopAgent = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'agent_stop' }))
    }
    resetState()
  }, [resetState])

  // ── Inject CSS styles on mount ──

  useEffect(() => {
    // Guard against duplicate style injection (StrictMode double-mount)
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style')
      style.id = STYLE_ID
      style.textContent = HIGHLIGHT_CSS
      document.head.appendChild(style)
    }

    return () => {
      // Only remove if no other instances are using it
      const existing = document.getElementById(STYLE_ID)
      if (existing) {
        existing.remove()
      }
    }
  }, [])

  // ── Connect WS on mount, cleanup on unmount ──

  useEffect(() => {
    mountedRef.current = true
    connect()

    return () => {
      mountedRef.current = false
      clearReconnectTimer()
      clearAutoHideTimer()
      clearHighlightTimers()
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current)
        pingTimerRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [connect, clearReconnectTimer, clearAutoHideTimer, clearHighlightTimers])

  return {
    isAgentActive,
    currentAgentPage,
    highlightCount,
    stopAgent,
    isConfirmOpen,
    confirmRequest,
    closeConfirmDialog,
    wsSend,
  }
}
